import { Check, Link2, Unlink } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/components/ui/dialog";
import { ScrollArea } from "~/components/ui/scroll-area";
import { Separator } from "~/components/ui/separator";
import { H3 } from "~/components/ui/typography";

import type { ConfidenceLevel } from "../../parts-similarity/utils/confidence";
import {
    CurrentPartDetails,
    ExpandableMatchCard,
    type CurrentPartDetailsProps,
    type MatchData,
} from "./part-review-shared";

// AIDEV-NOTE: Auto-link review dialog with left/right split layout
// Matches the design of SinglePartSimilarityReview for consistent UX

export interface InteractionHistoryItem {
    interactionId: string;
    name: string;
    description: string;
    recap?: string | null;
    quote?: string | null;
    attributes?: Array<{ name: string; content: string }> | null;
    sessionId: string;
    sessionTitle: string;
    sessionDate: string;
    isCurrentSession: boolean;
}

export interface AutoLinkReviewDialogProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    // Current part interaction data (from this session)
    currentPart: CurrentPartDetailsProps;
    // The linked part data with similarity info
    linkedPart: {
        partId: string;
        partName: string;
        partDescription?: string | null;
        confidence?: ConfidenceLevel | number;
        reasoning?: string;
        differences?: string | null;
        history: InteractionHistoryItem[];
    } | null;
    onConfirm: () => void;
    onUnlink: () => void;
    isConfirming: boolean;
    isUnlinking: boolean;
}

export function AutoLinkReviewDialog({
    isOpen,
    onOpenChange,
    currentPart,
    linkedPart,
    onConfirm,
    onUnlink,
    isConfirming,
    isUnlinking,
}: AutoLinkReviewDialogProps) {
    const isSubmitting = isConfirming || isUnlinking;

    // Build match data for the ExpandableMatchCard
    // Find the most recent non-current session interaction for expanded details
    const previousInteraction = linkedPart?.history.find(
        (h) => !h.isCurrentSession,
    );

    const matchData: MatchData | null = linkedPart
        ? {
              partId: linkedPart.partId,
              partName: linkedPart.partName,
              partDescription: linkedPart.partDescription,
              confidence: linkedPart.confidence ?? "high",
              reasoning: linkedPart.reasoning,
              differences: linkedPart.differences,
              // Use the most recent non-current session interaction for expanded view
              latestInteraction: previousInteraction
                  ? {
                        sessionDate: previousInteraction.sessionDate,
                        sessionTitle: previousInteraction.sessionTitle,
                        recap: previousInteraction.recap,
                        quote: previousInteraction.quote,
                        attributes: previousInteraction.attributes || undefined,
                    }
                  : undefined,
          }
        : null;

    return (
        <Dialog
            open={isOpen}
            onOpenChange={onOpenChange}
            analyticsId="auto-link-review-dialog"
        >
            <DialogContent
                className="flex max-h-[85vh] w-full sm:w-[95vw] 2xl:w-[80vw] sm:max-w-none flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Link2 className="size-5" />
                        Confirm Link
                    </DialogTitle>
                    <DialogDescription>
                        This part was automatically linked to previous sessions.
                        Is this the same part?
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-1 gap-4 min-h-0">
                    {/* Left side: Current part details */}
                    <div className="w-1/2 flex flex-col min-h-0">
                        <H3 className="text-sm font-medium text-muted-foreground mb-2">
                            This Session
                        </H3>
                        <CurrentPartDetails {...currentPart} />
                    </div>

                    <Separator orientation="vertical" />

                    {/* Right side: Linked part info */}
                    <div className="w-1/2 flex flex-col min-h-0 overflow-hidden">
                        <H3 className="text-sm font-medium text-muted-foreground mb-2">
                            Linked Part
                        </H3>
                        <ScrollArea className="flex-1">
                            <div className="pr-4">
                                {matchData && (
                                    <ExpandableMatchCard
                                        match={matchData}
                                        showCheckbox={false}
                                        defaultExpanded={true}
                                    />
                                )}
                            </div>
                        </ScrollArea>
                    </div>
                </div>

                <Separator />

                <DialogFooter className="flex justify-end gap-2 pt-2">
                    <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={onUnlink}
                        disabled={isSubmitting}
                    >
                        <Unlink className="size-4" />
                        {isUnlinking ? "Unlinking..." : "Not the same part"}
                    </Button>
                    <Button
                        size="sm"
                        className="gap-2"
                        onClick={onConfirm}
                        disabled={isSubmitting}
                        isLoading={isConfirming}
                    >
                        <Check className="size-4" />
                        {isConfirming ? "Confirming..." : "Confirm link"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
